import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import axiosInstance from "../../../../api/axiosInstance";
import { resolveAssetUrl } from "../../../../utils/resolveAssetUrl";
import { formatDistanceToNow } from "date-fns";
import logoImage from "../../../../assets/img/serviceloom-full.svg";
import { useHeaderSlot } from "../../../../contexts/HeaderSlotContext";
import {
  LuBell as BsBell,
  LuUser as FiUser,
  LuLogOut as FiLogOut,
  LuChevronDown as FiChevronDown,
} from "react-icons/lu";
import { getWsBaseUrl, getWsUrl } from "../../../../utils/wsUrl";

const toText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return value.content ?? value.message ?? value.name ?? value.title ?? value.token ?? JSON.stringify(value);
  }
  return "";
};

/* ─── NOTIFICATION BELL ─────────────────────────── */
const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    const fetchEmployeeId = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/v1/users/me`);
        const rawId = data.body;
        const id = typeof rawId === "string" ? rawId : rawId?.actual_id;
        if (id) setEmployeeId(id);
      } catch (err) {
        console.error("Error fetching employee ID:", err);
      }
    };
    fetchEmployeeId();
  }, []);

  useEffect(() => {
    if (!employeeId) return;

    const fetchNotifications = async () => {
      try {
        const res = await axiosInstance.get(`/api/v1/notifications/by-user/${employeeId}`);
        setNotifications(res.data.body || []);
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };
    fetchNotifications();

    const ws = new WebSocket(getWsUrl(employeeId));
    wsRef.current = ws;
    ws.onopen = () => console.log("✅ Notification WebSocket connected");
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "NOTIFICATION") {
          setNotifications((prev) => [
            { id: data.id, title: data.title, message: data.message, link: data.link, created_at: data.created_at, is_read: false },
            ...prev,
          ]);
        }
      } catch (err) {
        console.error("WS message error:", err);
      }
    };
    ws.onclose = () => console.log("Notification WebSocket closed");
    ws.onerror = (err) => console.error("WebSocket error:", err);

    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [employeeId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = async (notificationId) => {
    setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, is_read: true } : n));
    try {
      await axiosInstance.patch(`/api/v1/applications/requests/notifications/${notificationId}/read-count`);
    } catch (err) {
      setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, is_read: false } : n));
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter((n) => !n.is_read);
    if (!unread.length) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await Promise.all(unread.map((n) =>
      axiosInstance.patch(`/api/v1/applications/requests/notifications/${n.id}/read-count`).catch(() => {})
    ));
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) await markAsRead(notification.id);
    if (notification.link) {
      let path = notification.link;
      if (path.startsWith("/tickets/")) path = `/application/my-request/${path.split("/").pop()}`;
      else if (path.startsWith("/my-request/") || path.startsWith("/inbox/")) path = `/application${path}`;
      navigate(path);
      setOpen(false);
    }
  };

  const getRelativeTime = (ts) => {
    if (!ts) return "";
    try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); }
    catch { return ""; }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
      >
        <BsBell className="w-6 h-6 text-[#3B3D41]" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#3250FF] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-24px)] sm:w-[350px] bg-white rounded-[20px] p-3 border border-gray-100 shadow-[0_10px_35px_rgba(0,0,0,0.12)] z-50 overflow-hidden">
          <div className="flex items-center justify-between px-1 pb-3">
            <h3 className="text-[17px] font-bold text-[#1E293B]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-semibold text-[#603CFF] hover:text-[#4F33E0] transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <BsBell className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`p-2 rounded-xl cursor-pointer transition-all ${
                    !n.is_read
                      ? "bg-[#E7EBFF] hover:bg-[#E0E0FB]"
                      : "bg-white border border-gray-100 hover:bg-gray-50"
                  }`}
                >
                  <p className={`text-[13px] leading-snug font-medium ${!n.is_read ? "text-[#1E293B]" : "text-[#475569]"}`}>
                    {toText(n.message || n.title)}
                  </p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    {getRelativeTime(n.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>

          {notifications.length > 10 && (
            <div
              className="mt-3 pt-2 text-center text-xs font-semibold cursor-pointer text-[#603CFF] hover:text-[#4F33E0] transition-colors border-t border-gray-100"
              onClick={() => {
                navigate("/application/notifications");
                setOpen(false);
              }}
            >
              View all {notifications.length} notifications →
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── USER DROPDOWN ─────────────────────────────── */
const UserDropdown = ({ isOpen, onToggle, onLogout, navigate }) => {
  const dropdownRef = useRef(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    axiosInstance
      .get(`/api/v1/users/me`)
      .then(({ data }) => {
        const u = data?.body || data || {};
        setProfile(u);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onToggle]);

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  const name =
    profile?.actual_name ||
    profile?.name ||
    profile?.full_name ||
    storedUser.name ||
    storedUser.full_name ||
    "Employee";
  const email = profile?.email || storedUser.email || "";
  const role = profile?.role || storedUser.role || "Employee";
  const avatarUrl = profile?.avatar_url || profile?.avatar || profile?.profile_pic || profile?.profile_image || storedUser.avatar_url || storedUser.avatar || "";

  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => onToggle(!isOpen)}
        className="flex items-center px-2 py-1 gap-3 justify-between rounded-full bg-[#FFFFFFB2] border border-[#EBE7F8] hover:bg-white transition-colors"
      >
        <div className="w-9 h-9 rounded-full bg-[#3538CD] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
          {avatarUrl ? (
            <img src={resolveAssetUrl(avatarUrl)} alt={name} className="w-full h-full object-cover rounded-full" />
          ) : (
            initials || <FiUser className="w-4 h-4" />
          )}
        </div>
        <div className="hidden sm:flex flex-col items-start leading-[1.1] min-w-0 flex-1">
          <span className="text-sm font-bold text-[#1B1730] truncate w-full text-left">{name}</span>
          <span className="text-xs text-[#6B6478] font-normal truncate w-full text-left mt-0.5">{role}</span>
        </div>
        <FiChevronDown className={`w-4 h-4 text-[#1B1730] flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden">
          {/* User info */}
          <div className="px-4 py-3 flex items-center gap-3" style={{ background: "#FFFFFF", borderBottom: "1px solid #DDE3EA" }}>
            <div className="w-8 h-8 rounded-full bg-[#3538CD] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img src={resolveAssetUrl(avatarUrl)} alt={name} className="w-full h-full object-cover rounded-full" />
              ) : (
                initials
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
              {email && <p className="text-xs text-gray-500 truncate mt-0.5">{email}</p>}
            </div>
          </div>
          {/* Menu items */}
          <div className="py-1">
            <button
              onClick={() => {
                navigate("/application/my-profile");
                onToggle(false);
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-[#F3F5F7] transition-colors"
            >
              <FiUser className="w-4 h-4 text-gray-400" />
              My Profile
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <FiLogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── MAIN HEADER ──────────────────────────────── */
const ApplicationHeader = () => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const { slot } = useHeaderSlot();

  const handleLogout = () => {
    localStorage.clear();
    // DEF-178: chat/session state lives in sessionStorage, which
    // localStorage.clear() leaves untouched — clear it so the next user in
    // this tab can't resume the previous one's session.
    try { sessionStorage.clear(); } catch { /* unavailable — non-fatal */ }
    navigate("/auth/sign-in");
  };

  return (
    <header className="w-full bg-white border-b border-gray-200 flex-shrink-0">
      {/* Top row: Logo + (Nav on sm+) + Right Actions */}
      <div className="px-3 sm:px-6 h-14 flex items-center justify-between gap-3">

        {/* Left: Logo */}
        <div className="flex items-center gap-3 sm:gap-6 min-w-0">
          <img
            src={logoImage}
            alt="ServiceLoom"
            onClick={() =>
              navigate("/application/home", { state: { resetView: Date.now() } })
            }
            className="h-6 sm:h-7 w-auto flex-shrink-0 cursor-pointer"
          />
        </div>

        {/* Right: Bell + Avatar */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {slot && (
            <div className="flex items-center mr-1">
              {slot}
            </div>
          )}
          <NotificationBell />
          <div className="w-px h-5 bg-gray-200" />
          <UserDropdown
            isOpen={showDropdown}
            onToggle={setShowDropdown}
            onLogout={handleLogout}
            navigate={navigate}
          />
        </div>
      </div>
    </header>
  );
};

export default ApplicationHeader;
